import {
  type PermitRow,
  customerTags,
  formatIssuedDate,
  parseDescriptionFull,
} from '@mollusk/shared';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { NonBuildingBadge } from '../../components/NonBuildingBadge';
import { TagChip } from '../../components/TagChip';
import { MONO } from '../../theme';
import { useIsAdmin, visibleFields } from '../auth/internal';
import { permitDetailHref } from './permitDetailHref';
import { STATUS_DATE_HELP, STATUS_DATE_LABEL, isStatusDated, typeLabel } from './permitView';

/**
 * Every field, under its data-dictionary name and in the dictionary's order
 * (customer data dictionary, Permits sheet), so a prospect can match the popup
 * to their file (#171). Each field appears once.
 */
const DICTIONARY_FIELDS: ReadonlyArray<keyof PermitRow> = [
  'permit_id',
  'source_id',
  'source_permit_number',
  'permit_type',
  'permit_category',
  'permit_type_display',
  'permit_subtype',
  'permit_class',
  'work_type',
  'description',
  'tags',
  'applied_date',
  'issued_date',
  'permit_date',
  'permit_date_source',
  'final_date',
  'status_date',
  'expiration_date',
  'status_normalized',
  'total_fee',
  'address_line1',
  'address_line2',
  'city',
  'state',
  'postal_code',
  'parcel_apn',
  'latitude',
  'longitude',
  'address_geocodeable',
  'is_located',
  'applicant_name',
  'contractor_name',
  'owner_name',
  'description_full',
];

function fieldValue(permit: PermitRow, field: keyof PermitRow): string | null {
  const v = permit[field];
  if (v === null || v === undefined || (Array.isArray(v) && v.length === 0)) return null;
  if (field === 'issued_date')
    return formatIssuedDate(permit.issued_date, permit.issued_date_precision);
  if (field === 'status_date' && !isStatusDated(permit)) return null;
  return Array.isArray(v) ? v.join(', ') : String(v);
}

/** The published description, else the description line of description_full. */
function permitDescription(p: PermitRow): string | null {
  if (p.description) return p.description;
  const entry = parseDescriptionFull(p.description_full).find(
    (e) => e.kind === 'pair' && e.label === 'Description',
  );
  return entry ? entry.value : null;
}

function SummaryRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box sx={{ display: 'flex', gap: 2, py: 0.5 }}>
      <Typography variant="body2" color="text.secondary" sx={{ width: 110, flexShrink: 0 }}>
        {label}
      </Typography>
      <Box sx={{ minWidth: 0 }}>{children}</Box>
    </Box>
  );
}

export function PermitDetailModal({
  permit,
  onClose,
}: {
  permit: PermitRow | null;
  onClose: () => void;
}) {
  const isAdmin = useIsAdmin();
  const description = permit ? permitDescription(permit) : null;
  return (
    <Dialog open={!!permit} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {permit?.source_permit_number ? (
          <Box component="span" sx={{ fontFamily: MONO }}>
            {permit.source_permit_number}
          </Box>
        ) : (
          'Permit'
        )}
        <NonBuildingBadge permit={permit} />
      </DialogTitle>
      <DialogContent>
        {permit ? (
          <>
            <SummaryRow label="Type">
              <Typography variant="body2">{typeLabel(permit)}</Typography>
            </SummaryRow>
            <SummaryRow label="Description">
              <Typography variant="body2" sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                {description ?? '—'}
              </Typography>
            </SummaryRow>
            <SummaryRow label="Applied">
              <Typography variant="body2">{permit.applied_date ?? '—'}</Typography>
            </SummaryRow>
            <SummaryRow label="Issued">
              <Typography variant="body2">
                {formatIssuedDate(permit.issued_date, permit.issued_date_precision) ?? '—'}
              </Typography>
            </SummaryRow>
            <SummaryRow label="Final">
              <Typography variant="body2">{permit.final_date ?? '—'}</Typography>
            </SummaryRow>
            {isStatusDated(permit) ? (
              <SummaryRow label={STATUS_DATE_LABEL}>
                <Typography variant="body2" title={STATUS_DATE_HELP}>
                  {permit.status_date}
                </Typography>
              </SummaryRow>
            ) : null}
            <SummaryRow label="Contractor">
              <Typography variant="body2">{permit.contractor_name ?? '—'}</Typography>
            </SummaryRow>
            <SummaryRow label="Tags">
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {customerTags(permit.tags).length > 0
                  ? customerTags(permit.tags).map((t) => <TagChip key={t} tag={t} />)
                  : '—'}
              </Box>
            </SummaryRow>
            {isAdmin ? (
              <Button href={permitDetailHref(permit.source_id, permit.permit_id)} sx={{ mt: 1 }}>
                Open linked permit graph
              </Button>
            ) : null}
            <Accordion
              disableGutters
              sx={{ mt: 2 }}
              slotProps={{ transition: { unmountOnExit: true } }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>Show all fields</AccordionSummary>
              <AccordionDetails>
                <Table size="small">
                  <TableBody>
                    {visibleFields(DICTIONARY_FIELDS, isAdmin).map((field) => {
                      const v = fieldValue(permit, field);
                      if (v == null) return null;
                      return (
                        <TableRow key={field}>
                          <TableCell
                            component="th"
                            scope="row"
                            sx={{
                              fontFamily: MONO,
                              fontSize: 13,
                              width: 220,
                              verticalAlign: 'top',
                            }}
                          >
                            {field}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                            {v}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </AccordionDetails>
            </Accordion>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
